import { useState } from 'react';
import { FaChalkboardTeacher, FaCheckCircle, FaBook, FaUsers, FaClock, FaDollarSign } from 'react-icons/fa';
import { contactInfo } from '../data/siteData';
import { sanitizeFormData, validateEmail, validatePhone, validateText, validateMessage } from '../utils/helpers';
import './BecomeTeacher.css';

const BecomeTeacher = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    fatherName: '',
    email: '',
    whatsapp: '',
    address: '',
    country: '',
    education: '',
    specialization: '',
    experience: '',
    languages: [],
    courses: [],
    gender: '',
    description: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      const currentValues = formData[name] || [];
      if (checked) {
        setFormData({
          ...formData,
          [name]: [...currentValues, value]
        });
      } else {
        setFormData({
          ...formData,
          [name]: currentValues.filter(item => item !== value)
        });
      }
    } else if (type === 'radio') {
      setFormData({
        ...formData,
        [name]: value
      });
    } else {
      // Sanitize text input on change
      const sanitizedValue = typeof value === 'string' ? value.trim() : value;
      setFormData({
        ...formData,
        [name]: sanitizedValue
      });
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Security: Validate and check for malicious content
    if (!validateText(formData.fullName, { minLength: 2, maxLength: 100, required: true })) {
      newErrors.fullName = 'Please enter a valid full name (2-100 characters, no scripts)';
    }
    
    if (!validateText(formData.fatherName, { minLength: 2, maxLength: 100, required: true })) {
      newErrors.fatherName = 'Please enter a valid father name (2-100 characters, no scripts)';
    }
    
    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.whatsapp && !validatePhone(formData.whatsapp)) {
      newErrors.whatsapp = 'Please enter a valid WhatsApp number (7-15 digits)';
    }
    
    if (!validateText(formData.address, { minLength: 5, maxLength: 200, required: true })) {
      newErrors.address = 'Please enter a valid address (5-200 characters)';
    }
    
    if (!validateText(formData.country, { minLength: 2, maxLength: 100, required: true })) {
      newErrors.country = 'Please enter a valid country (2-100 characters)';
    }
    
    if (!validateText(formData.education, { minLength: 2, maxLength: 200, required: true })) {
      newErrors.education = 'Please enter a valid education (2-200 characters)';
    }
    
    if (!validateText(formData.specialization, { minLength: 2, maxLength: 200, required: true })) {
      newErrors.specialization = 'Please enter a valid specialization (2-200 characters)';
    }
    
    if (!validateText(formData.experience, { minLength: 1, maxLength: 50, required: true })) {
      newErrors.experience = 'Please enter valid experience';
    }
    
    if (formData.languages.length === 0) {
      newErrors.languages = 'Please select at least one language';
    }
    
    if (formData.courses.length === 0) {
      newErrors.courses = 'Please select at least one course';
    }
    
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }
    
    if (!validateMessage(formData.description, { minLength: 100, maxLength: 2000, required: true })) {
      const wordCount = formData.description.trim().split(/\s+/).length;
      if (wordCount < 100) {
        newErrors.description = 'Description must be at least 100 words';
      } else {
        newErrors.description = 'Description contains invalid content or exceeds maximum length';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Sanitize all form data before processing
      const sanitizedData = sanitizeFormData(formData);
      console.log('Teacher application submitted:', sanitizedData);
      
      // Create the WhatsApp message using sanitized data
      const message = `*Become a Teacher - New Application*

*Personal Information:*
*Full Name:* ${sanitizedData.fullName}
*Father Name:* ${sanitizedData.fatherName}
*Email:* ${sanitizedData.email}
*WhatsApp:* ${sanitizedData.whatsapp || 'Not provided'}
*Address:* ${sanitizedData.address}
*Country:* ${sanitizedData.country}
*Gender:* ${sanitizedData.gender}

*Education & Experience:*
*Education:* ${sanitizedData.education}
*Specialization:* ${sanitizedData.specialization}
*Experience:* ${sanitizedData.experience} years

*Languages:* ${sanitizedData.languages.join(', ') || 'Not specified'}
*Courses:* ${sanitizedData.courses.join(', ') || 'Not specified'}

*Description:*
${sanitizedData.description}

Please review this teacher application.`;

      // Encode the message for URL
      const encodedMessage = encodeURIComponent(message);
      
      // Get WhatsApp number from contactInfo and remove all non-digits
      const whatsappNumber = contactInfo.whatsapp.replace(/[^0-9]/g, '');
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      
      alert('Thank you for your application! Opening WhatsApp to send your application...');
      
      // Reset form
      setFormData({
        fullName: '',
        fatherName: '',
        email: '',
        whatsapp: '',
        address: '',
        country: '',
        education: '',
        specialization: '',
        experience: '',
        languages: [],
        courses: [],
        gender: '',
        description: ''
      });
      setErrors({});
    }
  };

  const languages = ['English', 'Urdu', 'Hindi', 'Arabic', 'Pashto'];
  
  const courses = [
    'Noorani Qaida Course',
    'Quran Memorization',
    'Ijazah Course',
    'Hadith Course',
    'Arabic Language Course',
    'Tajweed Course',
    'Islamic Studies Course',
    'Qiraat Course',
    'Tafseer Course',
    'Nazra Quran Course',
    'Quran Recitation Course',
    'Quran Translation Course',
    'Quran Reading Course'
  ];

  const benefits = [
    {
      icon: <FaClock />,
      title: 'Flexible Schedule',
      description: 'Work from home with flexible hours that fit your lifestyle'
    },
    {
      icon: <FaDollarSign />,
      title: 'Competitive Pay',
      description: 'Earn competitive rates with timely monthly payments'
    },
    {
      icon: <FaUsers />,
      title: 'Global Students',
      description: 'Teach students from around the world'
    },
    {
      icon: <FaBook />,
      title: 'Resources Provided',
      description: 'Access to teaching materials and ongoing support'
    }
  ];

  const requirements = [
    'Must be a Hafiz or have completed advanced Quranic studies',
    'Fluent in Arabic and at least one other language (English, Urdu, etc.)',
    'Ijazah or certification in Quran teaching (preferred)',
    'Strong internet connection and quiet teaching environment',
    'Passion for teaching and patience with students',
    'Available for at least 15 hours per week'
  ];

  const wordCount = formData.description.trim().split(/\s+/).filter(word => word.length > 0).length;

  return (
    <div className="become-teacher-page">
      {/* Hero Header */}
      <div className="teacher-hero">
        <div className="teacher-hero-overlay"></div>
        <div className="container">
          <div className="teacher-hero-content">
            <div className="teacher-hero-icon-wrapper">
              <FaChalkboardTeacher className="teacher-hero-icon" />
            </div>
            <span className="teacher-hero-badge">Join Our Team</span>
            <h1 className="teacher-hero-title">Become a Teacher</h1>
            <p className="teacher-hero-subtitle">
              Join our team and make a difference in spreading Quranic knowledge worldwide
            </p>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="intro-section">
            <h2>Why Teach With Us?</h2>
            <p>
              Join a global community of educators dedicated to teaching the Quran. 
              We provide a supportive environment where you can grow professionally 
              while making a meaningful impact on students' lives worldwide.
            </p>
          </div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="requirements-section">
            <h2>Requirements</h2>
            <ul className="requirements-list">
              {requirements.map((req, index) => (
                <li key={index}>
                  <FaCheckCircle /> {req}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="application-section">
        <div className="container">
          <div className="form-header">
            <h2>Apply Now</h2>
            <p className="form-intro">
              Fill out the form below and we'll get back to you within 3-5 business days.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="teacher-application-form">
            {/* Personal Information - Two Columns */}
            <div className="form-section">
              <h3 className="section-title">Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className={errors.fullName ? 'error' : ''}
                  />
                  {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                </div>

                <div className="form-group">
                  <label>
                    Father Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleChange}
                    placeholder="Father Name"
                    className={errors.fatherName ? 'error' : ''}
                  />
                  {errors.fatherName && <span className="error-message">{errors.fatherName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Your Whatsapp
                  </label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="Your Whatsapp"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Address <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Address"
                    className={errors.address ? 'error' : ''}
                  />
                  {errors.address && <span className="error-message">{errors.address}</span>}
                </div>

                <div className="form-group">
                  <label>
                    Country <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                    className={errors.country ? 'error' : ''}
                  />
                  {errors.country && <span className="error-message">{errors.country}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Education <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    placeholder="Education"
                    className={errors.education ? 'error' : ''}
                  />
                  {errors.education && <span className="error-message">{errors.education}</span>}
                </div>

                <div className="form-group">
                  <label>
                    Specialization <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    placeholder="Specialization"
                    className={errors.specialization ? 'error' : ''}
                  />
                  {errors.specialization && <span className="error-message">{errors.specialization}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Experience <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Experience"
                    className={errors.experience ? 'error' : ''}
                  />
                  {errors.experience && <span className="error-message">{errors.experience}</span>}
                </div>
              </div>
            </div>

            {/* Languages Section */}
            <div className="form-section">
              <h3 className="section-title">Languages</h3>
              <div className="checkbox-group">
                {languages.map(lang => (
                  <label key={lang} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="languages"
                      value={lang}
                      checked={formData.languages.includes(lang)}
                      onChange={handleChange}
                    />
                    <span className="checkbox-custom"></span>
                    {lang}
                  </label>
                ))}
              </div>
              {errors.languages && <span className="error-message">{errors.languages}</span>}
            </div>

            {/* Courses Section */}
            <div className="form-section">
              <h3 className="section-title">Which courses will you teach</h3>
              <div className="checkbox-group courses-checkbox-group">
                {courses.map(course => (
                  <label key={course} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="courses"
                      value={course}
                      checked={formData.courses.includes(course)}
                      onChange={handleChange}
                    />
                    <span className="checkbox-custom"></span>
                    {course}
                  </label>
                ))}
              </div>
              {errors.courses && <span className="error-message">{errors.courses}</span>}
            </div>

            {/* Gender Section */}
            <div className="form-section">
              <h3 className="section-title">
                Gender <span className="required">*</span>
              </h3>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={formData.gender === 'male'}
                    onChange={handleChange}
                  />
                  <span className="radio-custom"></span>
                  Male
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={formData.gender === 'female'}
                    onChange={handleChange}
                  />
                  <span className="radio-custom"></span>
                  Female
                </label>
              </div>
              {errors.gender && <span className="error-message">{errors.gender}</span>}
            </div>

            {/* Description Section */}
            <div className="form-section">
              <div className="description-label">
                <span>
                  Write Description about you atleast 100 words <span className="required">*</span>
                </span>
                <span className="word-count">({wordCount}/100 words)</span>
              </div>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="6"
                placeholder="write about your self"
                className={errors.description ? 'error' : ''}
              ></textarea>
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>

            {/* Submit Button */}
            <div className="form-submit">
              <button type="submit" className="submit-btn">
                SUBMIT APPLICATION
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BecomeTeacher;
